import { useState } from "react";
import type { Claim } from "@/types/claims";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useClaimAssignees } from "@/hooks/use-claim";
import type {
    ClaimAssignee,
    UpdateClaimAccessRequestBlockedModulesEnum,
} from "@/api/generated/client/models";
import { Mail, Pencil, UserPlus, Users, X } from "lucide-react";
import { AssigneeEditDialog } from "@/components/claims/assignees/AssigneeEditDialog";
import { InviteUsersDialog } from "@/components/claims/assignees/InviteUsersDialog";
import {
    getBlockedModules,
    getNextBlockedModules,
} from "@/lib/claim-assignee-module-utils";
import { useClaimAssigneeInviteState } from "@/hooks/use-claim-assignee-invite-state";

interface ClaimAssigneePickerProps {
    claim: Claim;
}

/**
 * Compact assignee control for a claim row: shows current assignees and lets the
 * user add/remove members. Assignable users are only fetched when the popover opens.
 */
export function ClaimAssigneePicker({ claim }: ClaimAssigneePickerProps) {
    const [open, setOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [editingShareId, setEditingShareId] = useState<number | null>(null);
    const {
        selectedAssignableUserId,
        setSelectedAssignableUserId,
        inviteEmail,
        setInviteEmail,
        shareLevel,
        setShareLevel,
        hiddenModules,
        toggleHiddenModule,
        submitInvite,
    } = useClaimAssigneeInviteState();
    const canWrite = claim.canWrite ?? false;
    const {
        assignableUsers,
        isLoadingAssignable,
        isAssigning,
        isRevoking,
        isInvitingByEmail,
        assignUser,
        revokeUser,
        inviteByEmail,
        updateUserAccess,
        isUpdatingAccess,
    } = useClaimAssignees(claim.id, { assignableEnabled: isInviteModalOpen });

    const assignees: ClaimAssignee[] = claim.assignees ?? [];
    const editingAssignee = assignees.find((assignee) => assignee.shareId === editingShareId) ?? null;

    const handleAccessLevelChange = async (assignee: ClaimAssignee, nextLevel: "ro" | "rw") => {
        await updateUserAccess({
            shareId: assignee.shareId,
            accessLevel: nextLevel,
            blockedModules: getBlockedModules(assignee),
        });
    };

    const handleModuleToggle = async (
        assignee: ClaimAssignee,
        module: UpdateClaimAccessRequestBlockedModulesEnum,
        checked: boolean,
    ) => {
        await updateUserAccess({
            shareId: assignee.shareId,
            blockedModules: getNextBlockedModules(getBlockedModules(assignee), module, checked),
        });
    };

    const closeInviteModal = () => {
        if (isInvitingByEmail || isAssigning) return;
        setIsInviteModalOpen(false);
    };


    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 px-2 text-xs font-normal text-muted-foreground ${canWrite ? "hover:text-foreground" : "opacity-60"}`}
                    title={canWrite ? "Assigned users" : "Assigned users (read-only)"}
                >
                    {assignees.length > 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5" />
                            <span className="truncate max-w-[110px]">
                                {assignees[0].name || assignees[0].email}
                                {assignees.length > 1 && ` +${assignees.length - 1}`}
                            </span>
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5">
                            <UserPlus className="h-3.5 w-3.5" />
                            <span>Assign</span>
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                side="bottom"
                sideOffset={8}
                collisionPadding={8}
                align="end"
                className="max-h-[var(--radix-popover-content-available-height)] w-56 overflow-y-auto p-3 space-y-3"
            >
                <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Assigned Users</span>
                    {assignees.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                            {assignees.length}
                        </Badge>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => setOpen(false)}
                        aria-label="Close assigned users popover"
                        title="Close"
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>

                {assignees.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No users assigned.</p>
                ) : (
                    <div className="space-y-1.5">
                        {assignees.map((assignee) => (
                            <Badge
                                key={assignee.userId}
                                variant="secondary"
                                className="flex w-full items-center gap-1 text-xs py-1 px-2"
                            >
                                <span className="min-w-0 flex-1 truncate" title={assignee.name || assignee.email}>
                                    {assignee.name || assignee.email}
                                </span>
                                <div className="ml-auto flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-3.5 w-3.5 p-0 rounded-full"
                                        onClick={() => {
                                            setOpen(false);
                                            setEditingShareId(assignee.shareId);
                                        }}
                                        disabled={!canWrite}
                                        aria-label={`Edit ${assignee.name || assignee.email}`}
                                        title="Edit"
                                    >
                                        <Pencil className="h-2.5 w-2.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-3.5 w-3.5 p-0 hover:bg-destructive/20 rounded-full"
                                        onClick={() => revokeUser(assignee.shareId)}
                                        disabled={isRevoking || !canWrite}
                                        aria-label={`Remove ${assignee.name || assignee.email} from claim`}
                                        title="Remove user"
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </Button>
                                </div>
                            </Badge>
                        ))}
                    </div>
                )}

                {!canWrite && (
                    <p className="text-xs text-amber-700">Read-only access: changing assignees is blocked.</p>
                )}

                <div className="border-t pt-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-full"
                        onClick={() => {
                            if (!canWrite) return;
                            setIsInviteModalOpen(true);
                        }}
                        disabled={!canWrite}
                    >
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                        Invite Users
                    </Button>
                </div>
            </PopoverContent>
        </Popover>

            <AssigneeEditDialog
                assignee={editingAssignee}
                canWrite={canWrite}
                isUpdatingAccess={isUpdatingAccess}
                onClose={() => setEditingShareId(null)}
                onAccessLevelChange={handleAccessLevelChange}
                onModuleToggle={handleModuleToggle}
            />

            <InviteUsersDialog
                open={isInviteModalOpen}
                canWrite={canWrite}
                isAssigning={isAssigning}
                isInvitingByEmail={isInvitingByEmail}
                isLoadingAssignable={isLoadingAssignable}
                assignableUsers={assignableUsers}
                selectedAssignableUserId={selectedAssignableUserId}
                inviteEmail={inviteEmail}
                shareLevel={shareLevel}
                hiddenModules={hiddenModules}
                onSetSelectedAssignableUserId={setSelectedAssignableUserId}
                onSetInviteEmail={setInviteEmail}
                onSetShareLevel={setShareLevel}
                onToggleHiddenModule={toggleHiddenModule}
                onInvite={async () => {
                    await submitInvite({
                        assignUser,
                        inviteByEmail,
                        onSuccess: () => {
                            setIsInviteModalOpen(false);
                            setOpen(false);
                        },
                    });
                }}
                onRequestClose={closeInviteModal}
                onOpen={() => setIsInviteModalOpen(true)}
            />
        </>
    );
}
